import { useEffect, useRef, useState } from 'react';

interface UseModalCloseProtectionProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly hasUnsavedChanges?: boolean;
  readonly onBeforeClose?: () => Promise<boolean> | boolean;
}

export const useModalCloseProtection = ({
  isOpen,
  onClose,
  hasUnsavedChanges = false,
  onBeforeClose
}: UseModalCloseProtectionProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    // Returns true if another MODAL overlay with a higher z-index is open on top.
    // Allows a base modal (z-50) to ceder ante un modal hijo (z-[60]) sin configuración.
    // Solo cuenta overlays marcados con [data-modal-overlay]: ignora toasts, dropdowns,
    // tooltips y la alerta de celebración (fixed con z alto que NO son modales y que, de
    // contarse, bloquearían ESC/clic-fuera en todos los modales).
    const isOverlaidByHigherModal = (): boolean => {
      const backdrop = modalRef.current?.parentElement;
      if (!backdrop) return false;
      const currentZ = Number.parseInt(getComputedStyle(backdrop).zIndex) || 0;
      return Array.from(document.querySelectorAll<HTMLElement>('[data-modal-overlay="true"]')).some(el => {
        if (el === backdrop) return false;
        return (Number.parseInt(getComputedStyle(el).zIndex) || 0) > currentZ;
      });
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isOverlaidByHigherModal()) return;
        e.preventDefault();
        handleClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // `e.target` es `EventTarget`, no necesariamente un elemento: podría ser el documento.
      // Antes se afirmaba con un cast (`as Element`) y luego se cubría la mentira con `?.` en
      // cada uso de closest(), lo que dejaba a TypeScript creyendo que el `?.` sobraba. Se
      // comprueba de verdad una sola vez: si no es un elemento no se puede saber si el clic fue
      // dentro o fuera, y ante la duda no se cierra (perder un formulario a medio llenar es
      // peor que dejar un modal abierto de más).
      if (!(e.target instanceof Element)) return;
      const target = e.target;

      // 1. If a higher z-index modal is open on top, this modal should not respond.
      if (isOverlaidByHigherModal()) return;

      // 2. If any dropdown/portal is open, ignore — Radix renders an invisible
      // full-screen DismissableLayer that intercepts clicks inside the modal.
      if (document.querySelector('[data-dropdown-portal="true"]')) return;

      // 3. Los toasts viven fuera del modal pero son parte de la misma interacción: el toast de
      // error ofrece "Agregar un comentario". Tratar ese clic como "clic fuera" cerraría el
      // formulario (o pediría confirmar cambios sin guardar) justo encima del reporte.
      if (target.closest('[data-toast-container="true"]')) return;

      // 4. Check if click is inside the modal content.
      if (modalRef.current?.contains(target)) return;

      // 5. Don't close if target is part of a floating Radix portal.
      if (target.closest('[data-radix-dialog-overlay], [data-state], [role="listbox"], [role="option"], [role="combobox"]')) return;

      // 6. Close the modal.
      if (isOpen) {
        e.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // El bloqueo de scroll lo gestionan los shells (DialogShell/FullScreenShell)
    // vía utils/scrollLock, keyed en isMounted (cubre la animación de cierre) y con
    // contador para modales apilados + compensación de scrollbar.
  }, [isOpen, hasUnsavedChanges]);

  const handleClose = async () => {
    // Si hay cambios sin guardar, mostrar confirmación
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
      return;
    }

    // Si hay función de validación antes de cerrar
    if (onBeforeClose) {
      const canClose = await onBeforeClose();
      if (!canClose) {
        return;
      }
    }

    onClose();
  };

  const forceClose = () => {
    setShowCloseConfirmation(false);
    onClose();
  };

  const cancelClose = () => {
    setShowCloseConfirmation(false);
  };

  return {
    modalRef,
    showCloseConfirmation,
    handleClose,
    forceClose,
    cancelClose
  };
};
